// Pure tree-manipulation utilities used by PathCreationTableView.
// Ported from admin-v2/src/helpers/assignmentHelpers.js — keep logic intact, TypeScript only.

import type { CreateAssignmentPayload } from "@/api/assignments";

/**
 * Normalises a notifications-frequency value to lowercase for the Rails API.
 * Falls back to `fallback` (default: "none") when the value is null/undefined.
 */
export function normalizeNotificationsFrequency(
  value: string | null | undefined,
  fallback = "none"
): string {
  return typeof value === "string" ? value.toLowerCase() : fallback;
}

export const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export const reminderTextMap: Record<string, string> = {
  None: "No reminder emails will be sent.",
  Daily: "Learners will receive reminder emails every day until the assessment is complete.",
  Weekly: "Learners will receive reminder emails every week until the assessment is complete.",
  Monthly: "Learners will receive reminder emails every month until the assessment is complete.",
  Quarterly:
    "Learners will receive reminder emails every quarter until the assessment is complete.",
};

/** Sentinel the assignment API uses to mean "no due date". */
export const NO_DUE_DATE = "0001-01-01T00:00:00Z";

/**
 * Parses an assignment `dueDateAt` into a Date, tolerating both API contracts: the svc
 * returns an ISO string, the Rails single-record endpoint returns unix seconds. Returns
 * null for the sentinel, empty, non-positive, or otherwise invalid values.
 */
export const parseAssignmentDueDate = (value: string | number | null | undefined): Date | null => {
  if (value == null || value === "" || value === NO_DUE_DATE) return null;
  if (typeof value === "number") {
    if (value <= 0) return null;
    // Values below 1e12 are seconds (Rails); at/above are already milliseconds.
    const ms = value < 1e12 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Pre-computed values needed to build a full assignment-policy update payload. */
export interface AssignmentPolicyPayloadInput {
  name?: string;
  description?: string;
  activated?: boolean;
  /** Unix seconds, or null for "no fixed due date". */
  dueDateAt: number | null;
  duration: number;
  notificationsFrequency?: string;
  roleId?: string;
  enrollmentDelay?: number;
}

/**
 * Builds a full `updateAssignmentPolicyAsync` payload. The backend requires every field on
 * each update (a sparse payload 500s) — this is the single place that shape is assembled so
 * every caller (Settings tab, header status toggle, etc.) stays in sync with that contract.
 */
export function buildAssignmentPolicyPayload(
  input: AssignmentPolicyPayloadInput
): Partial<CreateAssignmentPayload> {
  return {
    name: (input.name ?? "").trim(),
    description: input.description ?? "",
    activated: input.activated ?? false,
    dueDateAt: input.dueDateAt,
    duration: input.duration,
    notificationsFrequency: normalizeNotificationsFrequency(input.notificationsFrequency),
    roleId: input.roleId,
    locale: "en",
    enrollmentDelay: input.enrollmentDelay ?? 0,
  };
}

export interface AssignmentProgressCounts {
  total: number;
  complete: number;
  inProgress: number;
  notStarted: number;
  abandoned: number;
}

/** Parses the raw `AssignmentPolicyData.progress` record into numeric counts. */
export function parseAssignmentProgress(
  progress: Record<string, unknown> | undefined
): AssignmentProgressCounts {
  const p = progress ?? {};
  return {
    total: Number(p["totalAssigned"] ?? 0),
    complete: Number(p["complete"] ?? 0),
    inProgress: Number(p["inProgress"] ?? 0),
    notStarted: Number(p["notStarted"] ?? 0),
    abandoned: Number(p["abandoned"] ?? 0),
  };
}

export interface SortableNode {
  key?: string | number | null;
  data: {
    name?: string;
    type?: string;
    topic?: string;
    score?: string | number;
    duration?: string | number;
    level?: string;
    [key: string]: unknown;
  };
  children?: SortableNode[];
}

export function sortTreeNodes(nodes: SortableNode[], field: string, order: number): SortableNode[] {
  if (!nodes || nodes.length === 0) return nodes;

  const getSortValue = (node: SortableNode, f: string): string | number => {
    const data = node.data;

    switch (f) {
      case "type":
        return (data.type as string) || "";
      case "topic":
        if (data.level === "topic") {
          return (data.name as string) || "";
        }
        return (data.topic as string) || "";
      case "score": {
        const scoreStr = data.score ?? "0";
        return parseInt(String(scoreStr).replace("%", ""), 10);
      }
      case "duration": {
        const durationStr = data.duration ?? "0";
        const match = String(durationStr).match(/(\d+)/);
        return match ? parseInt(match[1] ?? "0", 10) : 0;
      }
      case "name":
        return (data.name as string) || "";
      default:
        return (data[f] as string) || "";
    }
  };

  const getDescendantValues = (node: SortableNode, f: string): (string | number)[] => {
    const values: (string | number)[] = [];

    const collectValues = (currentNode: SortableNode) => {
      const value = getSortValue(currentNode, f);
      if (value && value !== "") {
        values.push(value);
      }
      currentNode.children?.forEach(collectValues);
    };

    collectValues(node);
    return values;
  };

  const shouldSortLevel = (ns: SortableNode[], f: string): boolean => {
    if (!ns.length) return false;

    const firstNodeLevel = ns[0]?.data?.level;

    switch (f) {
      case "topic":
        return firstNodeLevel === "topic" || firstNodeLevel === "lesson";
      case "type":
        return true;
      case "score":
        return firstNodeLevel === "topic" || firstNodeLevel === "lesson";
      case "duration":
        return true;
      case "name":
        return true;
      default:
        return ns.some(node => {
          const value = getSortValue(node, f);
          return value !== "" && value !== 0;
        });
    }
  };

  const getNodeSortValue = (node: SortableNode, f: string): string | number => {
    const directValue = getSortValue(node, f);

    if (f === "type" && (!directValue || directValue === "")) {
      const descendantValues = getDescendantValues(node, f);
      if (descendantValues.length > 0) {
        const typeCount: Record<string, number> = {};
        descendantValues.forEach(type => {
          typeCount[String(type)] = (typeCount[String(type)] ?? 0) + 1;
        });

        const sortedTypes = Object.entries(typeCount).sort((a, b) => {
          if (b[1] !== a[1]) return b[1] - a[1];
          return a[0].localeCompare(b[0]);
        });

        return sortedTypes[0]?.[0] ?? "";
      }
    }

    if (f === "duration" && (!directValue || directValue === 0)) {
      const descendantValues = getDescendantValues(node, f) as number[];
      if (descendantValues.length > 0) {
        return order === 1 ? Math.min(...descendantValues) : Math.max(...descendantValues);
      }
    }

    return directValue;
  };

  let sortedNodes = [...nodes];

  if (shouldSortLevel(nodes, field)) {
    sortedNodes = sortedNodes.sort((a, b) => {
      const aValue = getNodeSortValue(a, field);
      const bValue = getNodeSortValue(b, field);

      if ((!aValue || aValue === "") && (!bValue || bValue === "")) return 0;

      if (!aValue || aValue === "") return order === 1 ? 1 : -1;
      if (!bValue || bValue === "") return order === 1 ? -1 : 1;

      let comparison = 0;
      if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }

      return order === 1 ? comparison : -comparison;
    });
  }

  return sortedNodes.map(node => {
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: sortTreeNodes(node.children, field, order),
      };
    }
    return node;
  });
}
